import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ExternalLink, ShieldAlert, ChevronRight } from "lucide-react";
import { api, fmtEUR, API } from "../lib/api";
import { Reveal } from "../components/home/Sections";
import { RichText } from "../components/RichText";

const mediaUrl = (p, m) => `${API}/shop-media/${p.product_id}/${m.id}`;

export default function ProductPage() {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [error, setError] = useState("");
    const [active, setActive] = useState(0);

    useEffect(() => {
        setP(null);
        setError("");
        setActive(0);
        api.get(`/public/boutique/${slug}`)
            .then((r) => {
                setP(r.data);
                const consent = localStorage.getItem("bc_consent") === "yes";
                api.post(`/public/boutique/${r.data.product_id}/view`, {
                    consent,
                    visitor_id: localStorage.getItem("bc_vid") || "",
                }).catch(() => {});
            })
            .catch(() => setError("Ce produit n'est pas disponible."));
    }, [slug]);

    const buy = async (index) => {
        const consent = localStorage.getItem("bc_consent") === "yes";
        try {
            const { data } = await api.post(`/public/boutique/${p.product_id}/merchant-click`, { merchant_index: index, consent });
            window.open(data.url, "_blank", "noopener,noreferrer");
        } catch {
            /* lien indisponible */
        }
    };

    if (error) {
        return (
            <main className="bg-ivory min-h-screen pt-32 pb-24" data-testid="product-not-found">
                <div className="container-bc text-center">
                    <p className="font-heading font-bold text-ink text-2xl">{error}</p>
                    <Link to="/boutique" className="text-forest underline mt-4 inline-block" data-testid="back-to-shop">Retour à la boutique</Link>
                </div>
            </main>
        );
    }
    if (!p) return <main className="bg-ivory min-h-screen" aria-label="Chargement" />;

    const images = p.media.filter((m) => m.kind === "image");
    const videos = p.media.filter((m) => m.kind === "video");
    const gallery = [...images, ...videos];
    const current = gallery[Math.min(active, gallery.length - 1)];
    const merchants = p.merchants || [];

    return (
        <main className="bg-ivory min-h-screen pt-28 pb-24" data-testid="product-page">
            <div className="container-bc">
                <nav className="text-sm text-ink-soft flex items-center gap-1.5 mb-8" aria-label="Fil d'ariane">
                    <Link to="/boutique" className="hover:text-forest flex items-center gap-1" data-testid="breadcrumb-shop"><ArrowLeft size={14} /> Boutique</Link>
                    <ChevronRight size={13} />
                    <span className="text-ink font-medium">{p.name}</span>
                </nav>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                            <div className="card-light overflow-hidden" data-testid="product-gallery">
                                {current ? (
                                    current.kind === "video" ? (
                                        <video controls playsInline preload="metadata" className="w-full aspect-[4/3] bg-forest-deep object-cover" src={mediaUrl(p, current)} data-testid="product-video" />
                                    ) : (
                                        <img src={mediaUrl(p, current)} alt={current.title || p.name} className="w-full aspect-[4/3] object-cover" data-testid="product-main-image" />
                                    )
                                ) : (
                                    <div className="w-full aspect-[4/3] bg-pearl flex items-center justify-center text-ink-soft">Visuel à venir</div>
                                )}
                            </div>
                            {gallery.length > 1 && (
                                <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
                                    {gallery.map((m, i) => (
                                        <button key={m.id} onClick={() => setActive(i)} className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${i === active ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"}`} data-testid={`gallery-thumb-${i}`} aria-label={`Visuel ${i + 1}`}>
                                            {m.kind === "video" ? (
                                                <span className="w-full h-full bg-forest text-ivory text-[10px] flex items-center justify-center font-mono">VIDÉO</span>
                                            ) : (
                                                <img src={mediaUrl(p, m)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
                            <p className="eyebrow mb-3">{p.category}</p>
                            <h1 className="font-heading font-extrabold text-ink text-3xl sm:text-4xl tracking-tight text-balance" data-testid="product-name">{p.name}</h1>
                            <p className="text-ink-soft mt-4 leading-relaxed">{p.short_description}</p>

                            <div className="mt-7">
                                {p.price_cents != null ? (
                                    <p className="font-heading font-extrabold text-forest text-3xl" data-testid="product-price">{fmtEUR(p.price_cents, p.currency)} <span className="text-sm text-ink-soft font-body font-normal">TTC indicatif</span></p>
                                ) : (
                                    <p className="text-sm text-ink-soft" data-testid="product-price-pending">Prix affiché par le marchand partenaire.</p>
                                )}
                            </div>

                            <div className="mt-7 space-y-3" data-testid="merchant-buttons">
                                {merchants.length === 0 ? (
                                    <div className="rounded-2xl border-2 border-dashed border-gold/50 bg-pearl px-5 py-4 text-sm text-ink-soft" data-testid="merchant-pending">
                                        Lien d'achat bientôt disponible — la fiche marchande officielle est en cours de raccordement.
                                    </div>
                                ) : (
                                    merchants.map((m, i) => (
                                        <button key={i} onClick={() => buy(i)} className="w-full flex items-center justify-between bg-forest text-ivory rounded-full px-7 py-3.5 font-semibold hover:bg-forest-surface transition-colors" data-testid={`buy-button-${i}`}>
                                            {m.label || `Acheter sur ${m.name}`}
                                            <ExternalLink size={16} />
                                        </button>
                                    ))
                                )}
                                {merchants.some((m) => m.affiliate) && (
                                    <p className="text-[11px] text-ink-soft leading-relaxed">Certains liens sont des liens affiliés : BODYCOACHIA peut percevoir une commission, sans surcoût pour vous.</p>
                                )}
                            </div>

                            {(p.benefits || []).length > 0 && (
                                <ul className="mt-8 space-y-2.5">
                                    {p.benefits.map((b, i) => (
                                        <li key={i} className="flex items-start gap-2.5 text-sm text-ink">
                                            <Check size={16} className="text-gold-dark mt-0.5 shrink-0" /> {b}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </motion.div>
                    </div>
                </div>

                {(p.journey || []).length > 0 && (
                    <section className="mt-24" data-testid="product-journey">
                        <Reveal>
                            <h2 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl tracking-tight mb-10">De la pesée à l'accompagnement</h2>
                        </Reveal>
                        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {p.journey.map((j, i) => (
                                <Reveal key={i} delay={i * 0.07}>
                                    <li className="card-light p-6 h-full">
                                        <span className="font-mono text-gold-dark text-sm font-semibold">{String(i + 1).padStart(2, "0")}</span>
                                        <h3 className="font-heading font-bold text-ink text-lg mt-3">{j.title}</h3>
                                        <p className="text-ink-soft text-sm mt-2 leading-relaxed">{j.text}</p>
                                    </li>
                                </Reveal>
                            ))}
                        </ol>
                    </section>
                )}

                <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {(p.description || p.description_html) && (
                        <Reveal className="lg:col-span-7">
                            <h2 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl tracking-tight mb-6">Présentation</h2>
                            {p.description_html ? (
                                <RichText html={p.description_html} className="text-ink-soft" testid="product-description" />
                            ) : (
                                <div className="text-ink-soft leading-relaxed whitespace-pre-line" data-testid="product-description">{p.description}</div>
                            )}
                            <p className="mt-6 text-xs text-ink-soft border-l-2 border-gold pl-4 leading-relaxed flex gap-2">
                                <ShieldAlert size={28} className="text-gold-dark shrink-0" />
                                Les mesures de composition corporelle issues de la bioimpédancemétrie sont des estimations destinées au suivi de vos tendances. Elles ne constituent ni un diagnostic médical ni un avis de santé.
                            </p>
                        </Reveal>
                    )}
                    {(p.specs || []).length > 0 && (
                        <Reveal className="lg:col-span-5" delay={0.1}>
                            <div className="card-light p-7" data-testid="product-specs">
                                <h2 className="font-heading font-bold text-ink text-xl mb-5">Fiche technique</h2>
                                <dl className="divide-y divide-border">
                                    {p.specs.map((s, i) => (
                                        <div key={i} className="py-3 flex flex-col sm:flex-row sm:justify-between gap-1">
                                            <dt className="text-sm text-ink-soft">{s.label}</dt>
                                            <dd className="text-sm font-medium text-ink sm:text-right">{s.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </Reveal>
                    )}
                </div>
            </div>
        </main>
    );
}
